// Recipe-driven capability resolution for the dashboard shell. Pure
// functions, no React — testable without a jsdom pragma (the app has
// both jsdom and RTL available for component tests; see
// DelegationStatusControl.test.tsx for that shape). See ADR-0072.
//
// The pattern this replaces: screens defaulted to the facility branch
// while `useRecipe()` was still resolving (undefined), which fired a
// doomed /facility-dashboard request — and a 403 — for every
// non-facility org on first paint. Every capability check in this file
// treats "recipe not loaded yet" as its own state, never a fallback to
// a concrete vertical.

import { isUnitImportRole, type RecipeMeResponse, type Role } from "@bitacorapro/api-types";

export type HomeSurface = "loading" | "facility-overview" | "group-cartera" | "none";

export function isFacilityDashboardEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.dashboards?.facility === true;
}

export function isCarteraDashboardEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.dashboards?.cartera === true;
}

// Resolves which shell surface to render for `/`. Never branches on
// recipe.slug (§0.4(6)) — only on the dashboards capability primitives
// and rootEntity.
export function resolveHomeSurface(recipe: RecipeMeResponse | undefined): HomeSurface {
  if (recipe === undefined) return "loading";
  if (isFacilityDashboardEnabled(recipe)) return "facility-overview";
  if (recipe.rootEntity === "group") return "group-cartera";
  return "none";
}

// Whether the shell has any working surface for this recipe at all —
// the facility overview or the group cartera. `undefined` (recipe still
// loading) answers false here AND false in lacksDashboardSurface below:
// loading is its own state, never "has" and never "lacks".
export function hasDashboardSurface(recipe: RecipeMeResponse | undefined): boolean {
  const surface = resolveHomeSurface(recipe);
  return surface === "facility-overview" || surface === "group-cartera";
}

// True only once the recipe has loaded and resolves to no surface (an
// asset-rooted vertical with no `dashboards` key — residential_rental
// today). The shell then drops its nav and renders NoDashboardSurface
// instead of the routed screen. Never recipe.slug (ADR-0072).
export function lacksDashboardSurface(recipe: RecipeMeResponse | undefined): boolean {
  return resolveHomeSurface(recipe) === "none";
}

// Share-bundle mint / revoke / access-log-read are admin-gated on the
// wire (core-api's share-bundles.ts, `auth.role !== "admin"`). This
// mirrors that gate client-side so the FE never renders a control that
// fires a doomed 403 — same "UX-only, server re-checks" posture as
// RequireDashboardRole / RequirePlatformAdmin.
export function canManageShareBundles(role: Role | undefined): boolean {
  return role === "admin";
}

// ADR-0079. Gates the "checklists por tienda" admin panel — never on
// recipe.slug (§0.4(6)/ADR-0072), only on the per-reportType
// areaCatalogMode primitive. `recipe === undefined` (still loading)
// returns false here by design, same posture as
// isFacilityDashboardEnabled/isCarteraDashboardEnabled — callers that
// need to distinguish "loading" from "definitively absent" must check
// useRecipe().isLoading themselves, not infer it from this boolean.
export function isChecklistTemplateAdminEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.reportTypes.some((rt) => rt.areaCatalogMode === "org_checklist_template") ?? false;
}

// ADR-0079 Decision 7 — mirrors core-api's ORG_SCOPED_ROLES
// (scoped-assets.ts). This is a whole-surface gate, not just a write
// gate: the backend's GET /checklist-templates* routes also call
// requireOrgScopedRole, so store_manager/brand_manager 403 on the read
// list too, not only on PUT/PATCH. Gate the panel's existence (and the
// `enabled` flag on every query it fires) on this, not just its write
// affordances — a read-only degrade for non-editors is not buildable,
// the backend never returns data to them. No shared FE/BE role-set
// constant exists for this pair (DASHBOARD_SHELL_ROLE_KEYS in
// @bitacorapro/api-types is the 5-role dashboard-visibility superset,
// not this 2-role boundary) — same re-declaration precedent
// canManageShareBundles already sets for its own single-role check.
export function canManageChecklistTemplates(role: Role | undefined): boolean {
  return role === "admin" || role === "company_manager";
}

// Catalog-configuration surface (create/patch items, assign assets) —
// stays ORG_SCOPED_ROLES, unchanged by ADR-0086. No dashboard UI exists
// for this today (the catalog loads out-of-band, same precedent as
// checklist templates); reserved for when one does. Deliberately
// re-declared rather than sharing canManageChecklistTemplates's body:
// no shared FE role-set constant exists for this 2-role boundary, same
// re-declaration precedent canManageChecklistTemplates itself sets over
// canManageShareBundles.
export function canManageMaintenanceItems(role: Role | undefined): boolean {
  return role === "admin" || role === "company_manager";
}

// ADR-0086 — mirrors core-api's MAINTENANCE_EVENT_WRITER_ROLES
// (maintenance.ts). This is the mark-serviced write gate specifically,
// split out from canManageMaintenanceItems (the catalog-config gate,
// above) once the two stopped sharing a role set: a service mark is a
// witness's attestation to a third-party contractor's visit, not a
// claim about the marker's own work, so store_manager/facility_supervisor
// are admitted here on their own asset scope — the server re-derives
// that scope per request regardless of what this function renders.
export function canMarkMaintenanceServiced(role: Role | undefined): boolean {
  return (
    role === "admin" ||
    role === "company_manager" ||
    role === "store_manager" ||
    role === "facility_supervisor"
  );
}

// ADR-0144 §2 — the unit importer (/carga-unidades). Mirrors core-api's
// /assets/import/* gate: the recipe declares `unitProfile` (never
// recipe.slug, §0.4(6)) and the caller is in UNIT_IMPORT_ROLE_KEYS or is
// a platform admin. `recipe === undefined` (still loading) is false, same
// posture as the other capability checks here.
export function canImportUnits(
  recipe: RecipeMeResponse | undefined,
  role: Role | undefined,
  platformAdmin: boolean | undefined
): boolean {
  if (!recipe?.unitProfile) return false;
  return isUnitImportRole(role ?? "") || platformAdmin === true;
}
